import { MenuItem, Stack, TextField, Typography } from "@mui/material";

/**
 * A time, read by a person and stored unambiguously.
 *
 * ## Twelve in the interface, twenty-four in the row
 *
 * The kernel stores `"22:00"` because a schedule is compared, sorted and
 * wrapped around midnight, and `10:00 PM` is three tokens to parse before any
 * of that can happen. Nobody says "twenty-two hundred" out loud, so the
 * interface does not either.
 *
 * The conversion lives here and nowhere else. A screen that formatted its own
 * would be a second copy of the rule, and the first one to disagree would put
 * an agent's overnight window twelve hours out with no error anywhere.
 *
 * ## Three selects rather than a text field
 *
 * A free field accepts `9pm`, `21`, `9:00 p.m.` and `nine`, and has to refuse
 * three of them — which turns setting a schedule into a spelling test. Three
 * selects cannot be wrong, and they are reachable by keyboard without a date
 * library.
 *
 * ## Midnight and noon are where this goes wrong
 *
 * `00:xx` is **12 AM** and `12:xx` is **12 PM**, and the arithmetic for both
 * is the thing people get backwards: hour 0 displays as 12, hour 12 displays
 * as 12, and only the meridiem separates them. `hour % 12 || 12` is the whole
 * rule and it is here once.
 *
 * A minute somebody stored outside the five-minute grid — `09:07`, typed into
 * a native picker before this existed — stays selectable rather than being
 * silently rounded on first render.
 */

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

/** `"22:00"` → `{ hour: 10, minute: "00", meridiem: "PM" }`, or nulls. */
export function parseClock(value) {
  const text = String(value || "").trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(text);
  if (!match) return { hour: null, minute: null, meridiem: null };
  const h = Number(match[1]);
  if (h < 0 || h > 23) return { hour: null, minute: null, meridiem: null };
  return {
    // Midnight and noon both land on 12; the meridiem is what tells them
    // apart, which is why this is not `h % 12`.
    hour: h % 12 || 12,
    minute: match[2],
    meridiem: h < 12 ? "AM" : "PM",
  };
}

/** `{ 10, "00", "PM" }` → `"22:00"`. */
export function buildClock(hour, minute, meridiem) {
  const h12 = Number(hour) % 12;
  const h24 = meridiem === "PM" ? h12 + 12 : h12;
  return `${String(h24).padStart(2, "0")}:${minute}`;
}

/** `"22:00"` → `"10:00 PM"`, for anywhere a stored time is only displayed. */
export function readClock(value) {
  const { hour, minute, meridiem } = parseClock(value);
  if (hour === null) return "";
  return `${hour}:${minute} ${meridiem}`;
}

export default function Clock({ value, onChange, label = "time", disabled }) {
  const { hour, minute, meridiem } = parseClock(value);

  // Defaults so touching one select always produces a complete, valid time
  // rather than a half-set one the caller has to guard against.
  const h = hour ?? 9;
  const mi = minute ?? "00";
  const me = meridiem ?? "AM";

  const minutes = MINUTES.includes(mi) ? MINUTES : [...MINUTES, mi].sort();
  const emit = (nh, nmi, nme) => onChange(buildClock(nh, nmi, nme));

  const field = { minWidth: 72 };

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <TextField
        select
        size="small"
        value={h}
        disabled={disabled}
        onChange={(e) => emit(Number(e.target.value), mi, me)}
        sx={field}
        slotProps={{ htmlInput: { "aria-label": `${label} hour` } }}
      >
        {HOURS.map((x) => (
          <MenuItem key={x} value={x}>
            {x}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="body2" color="text.secondary">
        :
      </Typography>
      <TextField
        select
        size="small"
        value={mi}
        disabled={disabled}
        onChange={(e) => emit(h, e.target.value, me)}
        sx={field}
        slotProps={{ htmlInput: { "aria-label": `${label} minute` } }}
      >
        {minutes.map((x) => (
          <MenuItem key={x} value={x}>
            {x}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        value={me}
        disabled={disabled}
        onChange={(e) => emit(h, mi, e.target.value)}
        sx={field}
        slotProps={{ htmlInput: { "aria-label": `${label} AM or PM` } }}
      >
        {["AM", "PM"].map((x) => (
          <MenuItem key={x} value={x}>
            {x}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
